import type { Cart } from '@/features/storefront/api/storefront';
import type { ContentReader } from '@/features/storefront/content/useContent';
import { SHOP_NAMESPACE, contentKey } from '@/features/storefront/content/types';
import type { Section } from '@/features/storefront/sections/schema';
import { humaniseKey } from '@/lib/i18n/translate';

/**
 * What the basket may hold, shared by the cart page and the quick cart.
 *
 * Both steppers used to stop only at 99, so a line with one unit left could be
 * pushed to five and the shopper learnt otherwise only when the till refused
 * the order (QA storefront 2026-10-15). The server still has the last word -
 * it re-checks stock when the order is placed - but the basket no longer lets
 * a shopper build an order it already knows cannot be filled.
 */

/** The steppers' bounds. Zero is not among them: only Remove takes a line out. */
export const MIN_LINE_QUANTITY = 1;
export const MAX_LINE_QUANTITY = 99;

type CartLine = Cart['items'][number];

/** The most this line may be set to: what is in stock, within the steppers' bounds. */
export function lineMaxQuantity(item: Pick<CartLine, 'available'>): number {
  if (item.available === null) return MAX_LINE_QUANTITY;
  return Math.min(MAX_LINE_QUANTITY, Math.max(MIN_LINE_QUANTITY, item.available));
}

/** A quantity the shopper asked for, brought inside what this line may hold. */
export function clampLineQuantity(item: Pick<CartLine, 'available'>, next: number): number {
  return Math.min(lineMaxQuantity(item), Math.max(MIN_LINE_QUANTITY, Math.trunc(next) || 0));
}

/** Whether a line asks for more than the shop has. */
export function lineOverStock(item: Pick<CartLine, 'available' | 'quantity'>): boolean {
  return item.available !== null && item.quantity > item.available;
}

/** Whether any line does - the till is held until the shopper fixes it. */
export function cartOverStock(
  items: readonly Pick<CartLine, 'available' | 'quantity'>[],
): boolean {
  return items.some(lineOverStock);
}

/** How much more the cart needs to reach free delivery, or null when there is no such offer. */
export function freeShippingProgress(cart: Cart | undefined, threshold: string | null) {
  if (!cart || !threshold) return null;
  const goal = Number(threshold);
  if (!Number.isFinite(goal) || goal <= 0) return null;
  const spent = Math.max(0, Number(cart.subtotal) - Number(cart.discount_amount ?? 0));
  return {
    remaining: Math.max(0, goal - spent),
    ratio: Math.min(1, spent / goal),
  };
}

/**
 * Whether a banner promises free delivery.
 *
 * Themes ship a cart banner like "You're close to free delivery! Orders over
 * ₹999 ship free" as fixed copy, which then shows on every cart whatever the
 * store's threshold is - or whether it has one (QA storefront 2026-10-15). The
 * cart page puts the real figure in its place, or drops it.
 */
const FREE_DELIVERY = /free\s+(delivery|shipping)|ships?\s+free|मुफ़्त|मुफ्त/i;

export function isFreeDeliveryBanner(section: Section): boolean {
  return section.type === 'banner' && FREE_DELIVERY.test(section.text);
}

/**
 * A storefront string, or `fallback` while the shop's copy has no such key.
 *
 * For words added with a fix before their key reaches the copy catalogue: the
 * reader would otherwise show the key humanised ("Save address").
 */
export function shopCopy(
  text: ContentReader,
  key: string,
  fallback: string,
  vars?: Record<string, string | number>,
): string {
  const value = text(key, vars);
  if (value !== humaniseKey(contentKey(SHOP_NAMESPACE, key))) return value;
  return fallback.replace(/\{(\w+)\}/g, (whole, name: string) =>
    vars && name in vars ? String(vars[name]) : whole,
  );
}
